import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { kenoOutcome, type KenoBetType, type KenoDraw } from '@nexbit/types'
import { useT } from '@/i18n'
import { useBoard, type TableState } from '@/lib/board'
import { api } from '@/lib/api'
import {
  displayCountDown, formatPrice, summarize,
  type DrawnRound, type OddCode, type TrendKind,
} from '@/lib/keno-view'
import { BetLot } from './BetLot'
import { TrendLot } from './TrendLot'
import { RebetSection } from './RebetSection'

export type Status = 'closed' | 'start-betting' | 'stop-betting' | 'waiting-result' | 'open-result'

/** What a card's children read about the round on show. */
export interface Product {
  gameId: string
  title: string
  gCode: string
  drawId: number | null
  roundId: string | null
  previousDrawId: number | null
  status: Status
  displayCountDown: string
  payoutRates: TableState['payoutRates']
  winners: Partial<Record<OddCode, boolean>>
}

interface Round { roundId: string; drawId: number; resultAtMs: number; adoptedAt: number }

type Phase = 'betting' | 'reveal' | 'awaitingNext'

/** The reference waits this long for a draw before giving up on it and resyncing. */
const MAX_WAIT_RESULT_MS = 120_000
const FLIP_MS = 50
const NEXT_DRAW_DELAY_MS = 2000

function shuffled(n: number): number[] {
  const a = Array.from({ length: n }, (_, i) => i)
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[a[i], a[j]] = [a[j]!, a[i]!]
  }
  return a
}

export function GameLot({ table, nowMs }: { table: TableState; nowMs: number }) {
  const t = useT()
  const board = useBoard()

  const [round, setRound] = useState<Round | null>(null)
  const [phase, setPhase] = useState<Phase>('betting')
  const [previousDrawId, setPreviousDrawId] = useState<number | null>(null)
  const [balls, setBalls] = useState<(number | null)[]>([])
  const [flippingDrawId, setFlippingDrawId] = useState('')
  const [roadmap, setRoadmap] = useState<DrawnRound[] | null>(null)
  const [kind, setKind] = useState<TrendKind>('bs')
  const [collapsed, setCollapsed] = useState(false)
  const [trendActive, setTrendActive] = useState(false)
  const [snack, setSnack] = useState<{ show: boolean; won: boolean; text: string }>({ show: false, won: false, text: '' })

  const timers = useRef<number[]>([])
  const later = useCallback((fn: () => void, ms: number) => {
    const id = window.setTimeout(fn, ms)
    timers.current.push(id)
    return id
  }, [])
  useEffect(() => () => { timers.current.forEach((id) => window.clearTimeout(id)) }, [])

  // ── Status ─────────────────────────────────────────────────────────────────
  const countDown = round ? Math.ceil((round.resultAtMs - nowMs) / 1000) : 0
  const status: Status = !round
    ? 'closed'
    : phase !== 'betting'
      ? 'open-result'
      : countDown > table.closeSeconds
        ? 'start-betting'
        : countDown > 0 ? 'stop-betting' : 'waiting-result'

  // ── Taking up a round ──────────────────────────────────────────────────────
  const flipDraw = useCallback((label: string) => {
    for (let i = 0; i <= label.length; i++) later(() => setFlippingDrawId(label.slice(0, i)), i * FLIP_MS)
  }, [later])

  const adopt = useCallback((cur: NonNullable<TableState['current']>, first: boolean) => {
    setRound({ roundId: cur.roundId, drawId: cur.roundNumber, resultAtMs: Date.parse(cur.resultAt), adoptedAt: Date.now() })
    setPhase('betting')
    setPreviousDrawId(table.previous?.roundNumber ?? null)
    if (first && table.previous) setBalls(table.previous.draw.numbers)
    flipDraw(`${table.gCode}${cur.roundNumber}`)
  }, [table.previous, table.gCode, flipDraw])

  const tableCurrent = table.current
  const tablePrevious = table.previous
  useEffect(() => {
    const cur = tableCurrent
    if (!round) {
      if (cur) adopt(cur, true)
      return
    }
    if (!cur) return
    const newer = cur.roundNumber > round.drawId
    if (phase === 'awaitingNext' && newer) { adopt(cur, false); return }
    // Stuck waiting on a draw that never came (a missed tick, a restart): resync.
    if (phase === 'betting' && newer && Date.now() - round.adoptedAt > MAX_WAIT_RESULT_MS
      && round.resultAtMs < board.serverNow()) adopt(cur, false)
  }, [tableCurrent, round, phase, adopt, board])

  // A table switched off goes to "ตลาดปิด".
  useEffect(() => {
    if (!tableCurrent && round && phase === 'betting' && status !== 'waiting-result') setRound(null)
  }, [tableCurrent, round, phase, status])

  // ── Waiting for the draw ───────────────────────────────────────────────────
  const reveal = useCallback((draw: KenoDraw, r: Round) => {
    setPhase('reveal')
    const order = shuffled(draw.numbers.length)
    const shown: (number | null)[] = Array(draw.numbers.length).fill(null)
    setBalls(shown.slice())
    order.forEach((idx, step) => {
      later(() => {
        shown[idx] = draw.numbers[idx]!
        setBalls(shown.slice())
      }, (step + 1) * FLIP_MS)
    })
    const done = (order.length + 1) * FLIP_MS
    const mine = board.stakes[r.roundId]
    if (mine && Object.keys(mine).length) later(board.checkBalance, done)
    later(() => {
      if (mine && Object.keys(mine).length) {
        let back = 0
        let staked = 0
        for (const [bt, amt] of Object.entries(mine) as [KenoBetType, number][]) {
          staked += amt
          const o = kenoOutcome(bt, draw)
          if (o === 'won') back += amt * (table.payoutRates[bt] ?? 0)
          if (o === 'refunded') back += amt
        }
        // The reference shows the round's net result, signed: +19.00 on a 20 Big that won.
        const net = back - staked
        const won = net >= 0
        if (net > 0) board.playWin(); else board.playLoss()
        setSnack({ show: true, won, text: `${won ? '+' : '-'}${formatPrice(Math.abs(net))}` })
        later(() => setSnack({ show: false, won: false, text: '' }), 3000)
      }
      setPhase('awaitingNext')
      board.refreshTables()
    }, done + NEXT_DRAW_DELAY_MS)
  }, [board, later, table.payoutRates])

  useEffect(() => {
    if (!round || phase !== 'betting' || status !== 'waiting-result') return
    if (tablePrevious && tablePrevious.roundNumber === round.drawId) {
      reveal(tablePrevious.draw, round)
      return
    }
    const id = window.setInterval(board.refreshTables, 1000)
    return () => window.clearInterval(id)
  }, [round, phase, status, tablePrevious, reveal, board.refreshTables])

  useEffect(() => {
    if (phase !== 'awaitingNext') return
    const id = window.setInterval(board.refreshTables, 1000)
    return () => window.clearInterval(id)
  }, [phase, board.refreshTables])

  // ── Roadmap: 2 s after each new draw, retried until it carries the draw just made ──
  const drawId = round?.drawId ?? null
  useEffect(() => {
    if (drawId === null) return
    let cancelled = false
    let timer: number | undefined
    const load = () => {
      api.roadmap(table.gameId).then((rows) => {
        if (cancelled) return
        setRoadmap(rows)
        if (previousDrawId !== null && !rows.some((r) => r.roundNumber === previousDrawId)) timer = window.setTimeout(load, 2000)
      }).catch(() => { if (!cancelled) timer = window.setTimeout(load, 2000) })
    }
    timer = window.setTimeout(load, roadmap ? 2000 : 0)
    return () => { cancelled = true; window.clearTimeout(timer) }
    // Re-run per draw only; `roadmap` is read for the first-load delay, not a trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawId, table.gameId, previousDrawId])

  // ── View mode resets (the reference product.viewreset) ─────────────────────
  useEffect(() => {
    setCollapsed(board.displayMode === 'trend' && document.documentElement.clientWidth <= 767)
    setTrendActive(false)
  }, [board.displayMode])

  const summary = useMemo(() => summarize(balls), [balls])

  const product: Product = {
    gameId: table.gameId,
    title: table.name,
    gCode: table.gCode,
    drawId: round?.drawId ?? null,
    roundId: round?.roundId ?? null,
    previousDrawId,
    status,
    displayCountDown: displayCountDown(countDown),
    payoutRates: table.payoutRates,
    winners: summary.winners,
  }

  const cd = product.displayCountDown
  const hidden = board.hiddenIds.has(table.gameId)
  const modeClass = trendActive && board.displayMode === 'trend' ? '' : board.displayMode
  const simpleActive = (k: TrendKind | 'combo') => board.displayMode !== 'simple' || kind === k ? 'active' : ''

  return (
    <div id={`gamecont-${table.gameId}`} className={['theproduct', 'cell', 'large-4', 'medium-6', 'active', collapsed ? 'collapsed' : '', hidden ? 'hidden' : ''].filter(Boolean).join(' ')}>
      <div className={['product', status, modeClass].filter(Boolean).join(' ')}>
        <div className="fullscreencont" onClick={() => board.fullscreen(table.gameId)}>
          <div className="fullscreen" {...{ text: t('menufullscreen') }} />
        </div>{' '}
        <div className="gameheader" onClick={() => {
          if (document.documentElement.clientWidth <= 767) setCollapsed((c) => !c)
          if (board.displayMode === 'trend') setTrendActive((a) => !a)
        }}>
          <div style={{ float: 'left', lineHeight: '20px', padding: '5px 0 0' }}>
            <div className="gameheader-title">{table.name}</div>{' '}
            <div className="gameheader-drawno">
              {status === 'closed' ? t('MARCLOSED') : `${t('DRAW_NO')} ${flippingDrawId}`}
            </div>
          </div>{' '}
          <div className="minisum">{summary.sum ?? ''}</div>{' '}
          <div className="countdowncont">
            {[0, 1, 2, 3, 4].map((i) => (
              <span key={i} className={i === 2 ? 'cntdwncont thecolons' : 'cntdwncont'}>{cd.substring(i, i + 1)}</span>
            ))}
          </div>
        </div>{' '}
        <div className="toppanel">
          <div className="draw-cont">
            <div className="numtiles-cont">
              <div className="numtiles-cont-space draw-result">
                {Array.from({ length: 10 }, (_, k) => {
                  const a = balls[k] ?? null
                  const b = balls[10 + k] ?? null
                  return (
                    <div className="tilecolumn" key={k}>
                      <div className={a !== null && a > 0 ? 'tile active' : 'tile'}><span>{a ?? ''}</span></div>{' '}
                      <div className={b !== null && b > 0 ? 'tile active' : 'tile'}><span>{b ?? ''}</span></div>
                    </div>
                  )
                })}
              </div>
            </div>{' '}
            <div className="summaryspace">
              <div className="middlepanel">
                <div className="middlediv">
                  <div className="lr50 frow"><div>{t('o7_trend')}</div><div>{t('o9_trend')}</div></div>{' '}
                  <div className="lr50">
                    <div className={summary.winners.o7 ? 'winner' : ''}>{summary.dcnt ?? ''}</div>
                    <div className={summary.winners.o9 ? 'winner' : ''}>{summary.tcnt ?? ''}</div>
                  </div>
                </div>{' '}
                <div className="middlediv">
                  <div className="lr50 frow"><div>{t('o10_trend')}</div><div>{t('o12_trend')}</div></div>{' '}
                  <div className="lr50">
                    <div className={summary.winners.o10 ? 'winner' : ''}>{summary.sum === null ? '' : summary.hcnt}</div>
                    <div className={summary.winners.o12 ? 'winner' : ''}>{summary.sum === null ? '' : summary.lcnt}</div>
                  </div>
                </div>{' '}
                <div className="sum-summary">
                  <div className="sum-cont">
                    <div className="sum-txt">{t('SUM')}</div>{' '}
                    <div className="sum-amt">{summary.sum && summary.sum > 0 ? summary.sum : '-'}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>{' '}
        <div className="viewhistorycont" onClick={() => board.openWindow(`/results?ref=${table.gameId}`)}>
          <div className="viewhistory" {...{ text: t('menuviewhistory') }}>{t('menuviewhistory')}</div>
        </div>{' '}
        <div className="bottompanel">
          <RebetSection product={product} />{' '}
          <div className="product-betsection">
            {status !== 'closed' && (
              <div>
                <div className="betspace">
                  <div className="nomorebets">{t('NOMOREBETS')}</div><div className="drawingresult">{t('DRAWING')}</div>{' '}
                  {status === 'waiting-result' && (
                    <span className="nextdraw"><a onClick={board.refreshTables}>{t('NEXT_DRAW')}</a></span>
                  )}{' '}
                  <div className={['grid-x', 'small-up-2', simpleActive('bs')].filter(Boolean).join(' ')}>
                    <BetLot product={product} type="o1" range="[811~1410]" />
                    <BetLot product={product} type="o2" range="[210~810]" />
                  </div>{' '}
                  <div className={['grid-x', 'small-up-2', simpleActive('oe')].filter(Boolean).join(' ')}>
                    <BetLot product={product} type="o3" />
                    <BetLot product={product} type="o4" />
                  </div>{' '}
                  <div className={['grid-x', 'small-up-3', simpleActive('dt')].filter(Boolean).join(' ')}>
                    <BetLot product={product} type="o7" />
                    <BetLot product={product} type="o8" />
                    <BetLot product={product} type="o9" />
                  </div>{' '}
                  <div className={['grid-x', 'small-up-3', simpleActive('hl')].filter(Boolean).join(' ')}>
                    <BetLot product={product} type="o10" />
                    <BetLot product={product} type="o11" />
                    <BetLot product={product} type="o12" />
                  </div>{' '}
                  <div className={['grid-x', 'small-up-4', simpleActive('combo')].filter(Boolean).join(' ')}>
                    <BetLot product={product} type="o13" />
                    <BetLot product={product} type="o14" />
                    <BetLot product={product} type="o15" />
                    <BetLot product={product} type="o16" />
                  </div>{' '}
                  <div className={['grid-x', 'line3special', 'small-up-5', simpleActive('fivee')].filter(Boolean).join(' ')}>
                    <BetLot product={product} type="o17" range="[210~695]" />
                    <BetLot product={product} type="o18" range="[696~763]" />
                    <BetLot product={product} type="o19" range="[764~855]" />
                    <BetLot product={product} type="o20" range="[856~923]" />
                    <BetLot product={product} type="o21" range="[924~1410]" />
                  </div>
                </div>{' '}
                <div className="product-trendlot">
                  <TrendLot product={product} rounds={roadmap} kind={kind} onKind={setKind} />
                </div>
              </div>
            )}
          </div>
        </div>{' '}
        <div style={{ clear: 'both' }} />
      </div>{' '}
      <div className={['ui-snackbar-container', snack.show ? 'show' : '', snack.won ? 'won' : 'lose'].filter(Boolean).join(' ')}>
        <div className="ui-snackbar">
          <div className="ui-snackbar-text">{snack.text}</div>
        </div>
      </div>
    </div>
  )
}
